import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, ClipboardCheck, PackageCheck, Plus, Send, Truck } from 'lucide-react'
import { usePurchaseOrders } from '@/hooks/procurement'
import { useLowStockAlerts } from '@/hooks/inventory'
import { PURCHASE_STATUS_LABELS, type PurchaseOrderStatus } from '@/api/types/procurement'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { getApiErrorMessage } from '@/api/client'

const statusClass: Record<PurchaseOrderStatus, string> = {
  CREATED: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  ORDERED: 'bg-cyan-100 text-cyan-700',
  IN_TRANSIT: 'bg-violet-100 text-violet-700',
  PARTIALLY_RECEIVED: 'bg-orange-100 text-orange-700',
  RECEIVED: 'bg-emerald-100 text-emerald-700',
  CANCELLED: 'bg-red-100 text-red-700',
  CLOSED: 'bg-gray-100 text-gray-700',
}

const summaryTone = {
  amber: 'bg-amber-100 text-amber-700',
  blue: 'bg-blue-100 text-blue-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  violet: 'bg-violet-100 text-violet-700',
}

export default function ProcurementWorkbench() {
  const { data: orders = [], isLoading, error } = usePurchaseOrders()
  const { count: lowStockCount } = useLowStockAlerts()

  const summary = useMemo(
    () => ({
      pendingApproval: orders.filter((order) => order.status === 'CREATED').length,
      pendingOrder: orders.filter((order) => order.status === 'APPROVED').length,
      pendingShipment: orders.filter((order) => order.status === 'ORDERED').length,
      inTransit: orders.filter((order) => order.status === 'IN_TRANSIT').length,
    }),
    [orders]
  )

  const activeOrders = useMemo(
    () =>
      orders
        .filter((order) => !['RECEIVED', 'CANCELLED', 'CLOSED'].includes(order.status))
        .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
        .slice(0, 6),
    [orders]
  )

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载采购工作台..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <InlineError message={getApiErrorMessage(error)} />
      </div>
    )
  }

  const summaryCards = [
    {
      label: '待审批',
      description: '补货申请与采购单待确认',
      count: summary.pendingApproval,
      icon: ClipboardCheck,
      tone: 'amber' as const,
      href: '/supply-chain/purchase/list?status=CREATED',
    },
    {
      label: '待下单',
      description: '审批完成，等待采购执行',
      count: summary.pendingOrder,
      icon: Send,
      tone: 'blue' as const,
      href: '/supply-chain/purchase/list?status=APPROVED',
    },
    {
      label: '待发货',
      description: '已下单，等待供应商发运',
      count: summary.pendingShipment,
      icon: PackageCheck,
      tone: 'cyan' as const,
      href: '/supply-chain/purchase/list?status=ORDERED',
    },
    {
      label: '运输中',
      description: '跟进交期并安排到货收货',
      count: summary.inTransit,
      icon: Truck,
      tone: 'violet' as const,
      href: '/supply-chain/purchase/list?status=IN_TRANSIT',
    },
  ]

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">采购工作台</h1>
          <p className="mt-1 text-sm text-gray-500">聚焦审批、下单、发运与收货等采购待办</p>
        </div>
        <Link
          to="/supply-chain/purchase/create"
          className="flex shrink-0 items-center gap-2 rounded-xl bg-primary-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-600"
        >
          <Plus className="h-4 w-4" />
          新建采购单
        </Link>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map(({ icon: Icon, tone, ...card }) => (
          <Link
            key={card.label}
            to={card.href}
            className="group rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm text-gray-500">{card.label}</div>
                <div className="mt-2 text-3xl font-bold text-gray-900">{card.count}</div>
              </div>
              <div className={`rounded-xl p-2.5 ${summaryTone[tone]}`}>
                <Icon className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span>{card.description}</span>
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </div>
          </Link>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <section className="rounded-2xl border border-gray-100 bg-white shadow-sm xl:col-span-2">
          <div className="flex items-center justify-between border-b border-gray-100 p-5">
            <div>
              <h2 className="font-bold text-gray-900">进行中的采购订单</h2>
              <p className="mt-1 text-sm text-gray-500">优先跟进未完成的订单状态</p>
            </div>
            <Link to="/supply-chain/purchase/list" className="text-sm font-medium text-primary-600 hover:underline">
              查看全部
            </Link>
          </div>

          {activeOrders.length ? (
            <div className="divide-y divide-gray-100">
              {activeOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/supply-chain/purchase/${order.id}`}
                  className="flex items-center justify-between gap-4 p-5 transition hover:bg-gray-50"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{order.purchaseNo}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass[order.status]}`}>
                        {PURCHASE_STATUS_LABELS[order.status]}
                      </span>
                    </div>
                    <div className="mt-1 truncate text-sm text-gray-500">
                      {order.supplierName} · {order.items.length} 种物料 · 收货仓库 {order.warehouseCode || '-'}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-gray-400" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-10 text-center text-sm text-gray-500">暂无进行中的采购订单</div>
          )}
        </section>

        <section className="rounded-2xl border border-amber-100 bg-amber-50 p-5">
          <div className="flex h-full flex-col">
            <div>
              <div className="flex items-center gap-2 text-amber-800">
                <PackageCheck className="h-5 w-5" />
                <h2 className="font-bold">库存补货协同</h2>
              </div>
              <div className="mt-4 text-3xl font-bold text-amber-900">{lowStockCount}</div>
              <p className="mt-1 text-sm leading-6 text-amber-800">项物料低于安全库存，可从预警清单发起补货申请。</p>
            </div>
            <Link
              to="/supply-chain/inventory/alert"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-amber-800 shadow-sm transition hover:bg-amber-100"
            >
              查看库存预警
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}
